import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import Modal from '../../../shared/components/ui/Modal';
import Button from '../../../shared/components/ui/Button';
import DrawingFilesList from './DrawingFilesList';
import {
  deleteAccommodationDrawing,
  getAccommodationDrawings
} from '../../services/api/implementation/impl';
import { groupDrawingsByType, normalizeDrawings } from '../../utils/accommodationDrawingUtils';

const AccommodationDrawingViewModal = ({ accommodationId, accommodationName, onClose }) => {
  const queryClient = useQueryClient();
  const [drawingToDelete, setDrawingToDelete] = useState(null);

  const {
    data: drawingsRaw,
    isLoading,
    isError,
    refetch
  } = useQuery({
    queryKey: ['accommodationDrawings', accommodationId],
    queryFn: () => getAccommodationDrawings(accommodationId),
    enabled: !!accommodationId
  });

  const drawings = useMemo(() => normalizeDrawings(drawingsRaw), [drawingsRaw]);
  const groupedDrawings = useMemo(() => groupDrawingsByType(drawings), [drawings]);

  const { mutate: deleteDrawing, isPending: isDeleting } = useMutation({
    mutationFn: (drawing) => deleteAccommodationDrawing(accommodationId, drawing),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accommodationDrawings', accommodationId] });
      toast.success('Drawing deleted successfully');
      setDrawingToDelete(null);
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message || error?.message || 'Failed to delete drawing'
      );
    }
  });

  const handleConfirmDelete = () => {
    if (!drawingToDelete) return;
    deleteDrawing(drawingToDelete);
  };

  return (
    <>
      <Modal
        width={50}
        confirmOnClose={false}
        isDirty={false}
        name={`View drawings${accommodationName ? ` — ${accommodationName}` : ''}`}
        onClose={onClose}>
        <div className="flex flex-col gap-4">
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-10 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading drawings...
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-8">
              <p className="text-sm text-red-600">Failed to load drawings. Please try again.</p>
              <Button type="button" onClick={() => refetch()} className="!px-4 !py-2 !text-sm">
                Retry
              </Button>
            </div>
          ) : groupedDrawings.length === 0 ? (
            <p className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-10 text-center text-sm text-gray-500">
              No drawings uploaded yet.
            </p>
          ) : (
            <DrawingFilesList
              groupedDrawings={groupedDrawings}
              deletingDrawingId={isDeleting ? drawingToDelete?.id : null}
              onDeleteDrawing={setDrawingToDelete}
            />
          )}

          <div className="flex justify-end border-t border-gray-100 pt-4">
            <Button type="button" onClick={onClose} displayType="secondary">
              Close
            </Button>
          </div>
        </div>
      </Modal>

      {drawingToDelete ? (
        <Modal width={40} name="Delete drawing" onClose={() => setDrawingToDelete(null)}>
          <div className="flex flex-col items-center">
            <h2 className="text-center text-lg font-medium text-primary">
              Are you sure you want to delete this drawing?
            </h2>
            <p className="mt-3 text-center text-sm text-gray-600 break-all">{drawingToDelete.name}</p>
            <div className="mt-8 flex w-full justify-end gap-3">
              <Button
                type="button"
                displayType="secondary"
                onClick={() => setDrawingToDelete(null)}
                disabled={isDeleting}>
                Cancel
              </Button>
              <Button
                type="button"
                displayType="primary-red"
                disabled={isDeleting}
                isLoading={isDeleting}
                onClick={handleConfirmDelete}>
                Delete
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </>
  );
};

export default AccommodationDrawingViewModal;
